import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import {Card} from "react-bootstrap";
import {DatePicker, LocalizationProvider, MobileDatePicker, DesktopTimePicker} from '@mui/x-date-pickers';
import {translateService, Translate} from "../../services/TranslateService";
import moment from "moment";
import {enUS, fiFI} from '@mui/x-date-pickers/locales';
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment';
import 'moment/dist/locale/fi';
import {ThemeProvider} from "@mui/material/styles";
import {useSelector} from "react-redux";
import {GlobalStore} from "../../reduxStore";
import styles from "./VehicleSearch.module.scss";
import {DataContext, ISearchParameters} from "../../contexts/dataContext";
import Select from 'react-select'
import type { Theme } from 'react-select'
import {Dialog} from "@mui/material";
import {Check24rCookie, GetCustomerBehaviourParams} from "../../services/CustomerBehaviourData";
import {extractCityFromPlace} from "../../services/extractCityFromPlace";
import {VehicleSegment} from "../../services/vehicleSegment";
import CustomTooltip from "../CustomTooltip";
import Autocomplete from "react-google-autocomplete";
import SvgIcon from '@mui/material/SvgIcon';
import {defaultConfig} from "../../config"
import LandBot from "landbot";
import {yellowTheme} from "../../CustomModalStyles";
import TopLevelTeaser from "./SearchTopTeaser";
import {useClarityStep} from "../../services/clarity/useClarityStep";
import {OneWayFirstView2} from "./OneWayFirstView2";

const owLandbotConfig = defaultConfig.landbot.oneway;

declare type SearchProps = {
    setSearchParams: Function;
    setErrorMsg: Function;
    clearResults: Function;
    modifyReservation?: boolean;
}

function MuiDateTimeIcons() {
    return ( // empty icon to make mui default invisible
        <SvgIcon></SvgIcon>
    );
}

const VehicleSearch = (props: SearchProps) => {
    const {user, searchParameters, changeSearchParameters, oneWayRoutes, oneWayRes,
        changeResType, enableOneWay, changeMapData, mapData, sendGa4Event, showDefaultResults,
        changeShowDefaultResults, loggedIn, changeFilteredList, mobileDevice, rootUrl,
        startDateInPast, addSafeDrivePackage, clearSelections, redirectRef,
        changeShowingModelsResults, changeModelId, changeSortingTab} = useContext(DataContext)
    const oneWayDefaultViewed = sessionStorage.getItem('oneWaySearchInit') ?? undefined;
    const language = useSelector((state: GlobalStore) => state.chauffeurReducer.deviceLanguage);
    const [isAddressSelected, setIsAddressSelected] = useState(!!searchParameters?.location);
    const startDate = moment(searchParameters?.startDate).seconds(0).milliseconds(0).clone()
    const endDate = searchParameters?.endDate ? moment(searchParameters?.endDate).seconds(0).milliseconds(0).clone() :
        moment().seconds(0).milliseconds(0).add(2, 'hours'); // if redirected from created res payment screen --> searchParameters.endDate will be undefined, hence using current time + 2 hours
    const defaultPickUpCity = searchParameters?.pickupCity;
    const defaultReturnCity = searchParameters?.returnCity;
    const [pickupDate, setPickupDate] = useState<moment.Moment>(startDate);
    const [returnDate, setReturnDate] = useState<moment.Moment>(endDate);
    const [returnError, setReturnError] = useState<string | null>(null);
    const [pickupError, setPickupError] = useState<string | null>(null);
    const [location, setLocation] = useState<any>(searchParameters?.location);
    const [disableButton, setDisableButton] = useState<boolean>(false);
    const [isOneWayRes, setIsOneWayRes] = useState<boolean | undefined>(oneWayRes);
    const [oneWaySelectOptionsPickup, setOneWaySelectOptionsPickup] = useState<any>([]);
    const [oneWaySelectOptionsReturn, setOneWaySelectOptionsReturn] = useState<any>([]);
    const [pickupCityChangeByDD, setPickupCityChangeByDD] = useState<boolean>(false);
    const [oneWayPickupCity, setOneWayPickupCity] = useState<{ value: string, label: string, isDisabled: boolean }>(defaultPickUpCity ? {value: defaultPickUpCity, label: defaultPickUpCity, isDisabled: true} :
        {value: '', label: translateService.translate('vehicleSearch.selectPickupCity'), isDisabled: true}); // works with react-select
    const [oneWayReturnCity, setOneWayReturnCity] = useState<{ value: string, label: string, isDisabled: boolean }>(defaultReturnCity ? {value: defaultReturnCity, label: defaultReturnCity, isDisabled: true} :
        {value: '', label: translateService.translate('vehicleSearch.selectReturnCity'), isDisabled: true});
    const PopulateReturnCityList = useCallback((oneWayRoutesArray: any, pickupCityValue: string) => {
        // setting corresponding return cities for selected pickup city
        let returnCities = [{value: '', label: translateService.translate('vehicleSearch.selectReturnCity'), isDisabled: true}];
        let returnCitiesList = oneWayRoutesArray[pickupCityValue];
        returnCitiesList.sort();
        for (let j=0; j < returnCitiesList.length; j++) {
            returnCities.push({value: oneWayRoutesArray[pickupCityValue][j],
                label: oneWayRoutesArray[pickupCityValue][j], isDisabled: false});
        }
        setOneWaySelectOptionsReturn(returnCities);
    }, []);
    const r24CookieVal = Check24rCookie();
    const [isLocationEmpty, setIsLocationEmpty] = useState(true);
    const customerBehaviorData = GetCustomerBehaviourParams();
    const [isPickupDatePickerOpen, setPickupDatePickerOpen] = useState(false);
    const [isPickupTimePickerOpen, setPickupTimePickerOpen] = useState(false);
    const [isReturnDatePickerOpen, setReturnDatePickerOpen] = useState(false);
    const [isReturnTimePickerOpen, setReturnTimePickerOpen] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [hasPopupShown, setHasPopupShown] = useState(false);
    const [lat, setLat] = useState<number | null>(searchParameters?.lat || null);
    const [lng, setLng] = useState<number | null>(searchParameters?.lon || null);
    const autoCompleteRef = useRef<HTMLInputElement>(null); // used to control search input field when geocoded value not selected + search clicked
    // Random per-session name on the location input — defeats Chrome's form-history autofill (it keys saved values by URL + field name)
    const [locationFieldName] = useState(() => `rrm-loc-${Math.random().toString(36).slice(2, 10)}`);
    const [userLocationSearchString, setUserLocationSearchString] = useState('');
    const landBotReady = sessionStorage.getItem('landBotReady') === 'true' && !sessionStorage.getItem('landBotUsage');
    const [showNoPickupCityMatch, setShowNoPickupCityMatch] = useState(false);
    const launcherMode = redirectRef === 'launcher' ? '1' : undefined;
    const showTopTeaser = sessionStorage.getItem('searchInit') ?? undefined;
    const [mapsReady, setMapsReady] = useState(!!window.google?.maps?.places);

    useEffect(() => {
        if (mapsReady) return;
        const interval = setInterval(() => {
            if (window.google?.maps?.places) {
                const currentValue = (document.getElementById('google-search-location') as HTMLInputElement)?.value;
                if (currentValue) setUserLocationSearchString(currentValue);
                setMapsReady(true);
                clearInterval(interval);
            }
        }, 100);
        return () => clearInterval(interval);
    }, [mapsReady]);

    useClarityStep('search', {
        city: location?.formatted_address ?? (location ?? ''),
        start: pickupDate.toString() || '',
        end: returnDate.toString() || '',
        oneway: isOneWayRes,
        loggedIn: loggedIn,
        safeDrive: addSafeDrivePackage ?? false
    });

    const handleOneWayCityChange = useCallback((city: any, selectType: string) => {
        if(selectType === 'pickup') {
            setOneWayPickupCity(city);
            setPickupCityChangeByDD(true);
        } else { setOneWayReturnCity(city); }
    }, []);

    const clearInput = () => {
        // must clear both ref value & google input element value -->
        // clearing ref only does not remove the text from the component on clear
        // (<Autocomplete/> lib qwerk workaround to clear field)
        const searchInput = document.getElementById('google-search-location') as HTMLInputElement;
        searchInput.value = "";
        props.setSearchParams((params: ISearchParameters) => {
            return {...params, location: null, lat: 0, lon: 0};
        })
        setLat(null);
        setLng(null);
        setLocation(null)
        setIsAddressSelected(false);
        handleSearchChanges();
        setUserLocationSearchString('');
        if (autoCompleteRef?.current?.value) {
            autoCompleteRef.current.value = "";
        }
        // note: full location reset, url param can be used again on reload
    };

    const handleSearchChanges = () => {
        if (showDefaultResults) {
            // removes default results view if location search changes
            changeShowDefaultResults(false);
            changeMapData([]);
        }
    }

    const handleQuickSearch = (pickupCityValue: string, returnCityValue: string) => {
        const now = moment();
        const dayOfWeek = now.isoWeekday(); // 1=Mon, 2=Tue, ...
        const daysUntilTuesday = dayOfWeek <= 2 ? 2 - dayOfWeek : 9 - dayOfWeek;
        const nextTuesday = now.clone().add(daysUntilTuesday === 0 ? 7 : daysUntilTuesday, 'days')
            .hours(9).minutes(0).seconds(0).milliseconds(0);
        const endMoment = nextTuesday.clone().add(2, 'hours');

        handleSearchChanges();
        if (!sessionStorage.getItem('searchInit')) {
            sessionStorage.setItem('searchInit', '1');
        }
        if (!sessionStorage.getItem('oneWaySearchInit')) {
            sessionStorage.setItem('oneWaySearchInit', 'true');
        }

        setPickupDate(nextTuesday);
        setReturnDate(endMoment);
        setOneWayPickupCity({value: pickupCityValue, label: pickupCityValue, isDisabled: true});
        setOneWayReturnCity({value: returnCityValue, label: returnCityValue, isDisabled: true});

        props.setSearchParams((params: ISearchParameters) => ({
            ...params,
            startDate: nextTuesday,
            startTime: '09:00',
            endDate: endMoment,
            endTime: '11:00',
            km: 100,
            showStart: 0,
            showAmount: 10,
            pickupCity: pickupCityValue,
            returnCity: returnCityValue,
            carId: null,
            cid: user?.id ?? customerBehaviorData.cid,
            utm_campaign: customerBehaviorData.utm_campaign,
            utm_source: customerBehaviorData.utm_source,
            utm_term: customerBehaviorData.utm_term,
            utm_medium: customerBehaviorData.utm_medium,
            utm_content: customerBehaviorData.utm_content,
            gclid: customerBehaviorData.gclid,
            cookie: r24CookieVal
        }));
    }

    const matchLocationToOneWay = () => {
        // if a2a location is an option for oneway pickup, default pickup will have it set as default
        const a2aLocation = getDefaultLocationValue();
        if (a2aLocation.length) {
            const locationBeforeComma = a2aLocation.split(',')[0].trim(); // get 'city' value (before comma)
            const oneWayPickupCities = Object.keys(oneWayRoutes ?? {});

            if (oneWayPickupCities.includes(locationBeforeComma)) {
                return locationBeforeComma;
            } else {
                return "NO_MATCH";
            }
        }
        return undefined;
    }

    const getDefaultLocationValue = (): string => {
        // google input has value (1st priority), user selected value (2nd priority), search parameters (final value)
        const searchValue = (document.getElementById('google-search-location') as HTMLInputElement)?.value;
        if (userLocationSearchString || searchValue) {
            return searchValue ?? userLocationSearchString; // use location selected by user first
        }

        if (searchParameters?.location) {
            if(searchParameters?.location?.formatted_address) {
                return searchParameters?.location?.formatted_address;
            } else {
                return searchParameters.location;
            }
        }
        return '';
    }

    useEffect(() => {
        if(mapData) {
            // Post-search initializer for the results list. Respect the chosen car/van segment
            // so a van search shows vans and a car search excludes vans (minibus/bus count as
            // car). Uses vehicleModelData.type directly — no dependency on acrissCodeSet timing.
            const seg = searchParameters?.vehicleSegment;
            const list = seg === 'van'
                ? mapData.filter((v) => v?.vehicleModelData?.type === 'van')
                : seg === 'car'
                    ? mapData.filter((v) => v?.vehicleModelData?.type !== 'van')
                    : mapData;
            changeFilteredList(list);
        }
    }, [mapData, searchParameters?.vehicleSegment]);

    useEffect(() => {
        // if one way res set from url params - this useEffect allows isOneWayRes state to be updated after initial state declaration
        if(isOneWayRes !== oneWayRes) {
            setIsOneWayRes(oneWayRes);
        }
    }, [oneWayRes]);

    useEffect(() => {
        props.clearResults();
    }, [location, pickupDate, returnDate, oneWayPickupCity, oneWayReturnCity]);

    useEffect(() => {
        setPickupDate(startDate)
        setReturnDate(endDate)
        if (searchParameters?.location) {
            setLocation(searchParameters?.location);
            setLat(searchParameters?.lat);
            setLng(searchParameters?.lon);
            if (!isAddressSelected) {
                setIsAddressSelected(!!searchParameters?.location);
            }
        }
    }, [searchParameters]);

    useEffect(() => {
        // effects from changing between OneWay & a2a type reservations
        // clears previous one-way city selections
        const defaultPickUpSelection = !Object.keys(oneWayRoutes ?? {})?.length ? undefined : matchLocationToOneWay(); // wait for one way city names and useEffect will retrigger this check
        if (!isOneWayRes) { // on resets on checkbox change, not on page reload
            setOneWayPickupCity({ value: '', label: translateService.translate('vehicleSearch.selectPickupCity'), isDisabled: true });
            setOneWayReturnCity({ value: '', label: translateService.translate('vehicleSearch.selectReturnCity'), isDisabled: true });
            changeSearchParameters({ ...searchParameters, pickupCity: '', returnCity: '' });
        } else if (defaultPickUpSelection) {
            if (defaultPickUpSelection === "NO_MATCH") {
                setShowNoPickupCityMatch(true);
                return;
            }
            handleOneWayCityChange({ value: defaultPickUpSelection, label: defaultPickUpSelection, isDisabled: false }, 'pickup');
        }
    }, [isOneWayRes, oneWayRoutes]);

    useEffect(() => {
        setIsLocationEmpty(!location);
        let locationFromUrl = sessionStorage.getItem('locationFromUrl');

        if (locationFromUrl === '1') { // only needed once
            setHasPopupShown(true);
            sessionStorage.removeItem('locationFromUrl');
        }
    }, [location]);

    useEffect(() => {
        // sets city data for one way pick up selections
        if (Object.keys(oneWayRoutes ?? {}).length > 0) {
            let pickUpCities = Object.keys(oneWayRoutes);
            pickUpCities.sort(); // to alphabetical order
            let pickUpCitiesArray = [{value: '', label: translateService.translate('vehicleSearch.selectPickupCity'), isDisabled: true}];
            for(let i=0; i < pickUpCities.length; i++) {
                pickUpCitiesArray.push({value: pickUpCities[i], label: pickUpCities[i], isDisabled: false});
            }
            setOneWaySelectOptionsPickup(pickUpCitiesArray);

            // check if last saved pickup location is valid (case for reload/refresh/onBack page)
            if(oneWayPickupCity.value.length > 0) {
                if(!Object.keys(oneWayRoutes).includes(oneWayPickupCity.value)){
                    setOneWayPickupCity({value: '', label: translateService.translate('vehicleSearch.selectPickupCity'), isDisabled: true});
                    setOneWayReturnCity({value: '', label: translateService.translate('vehicleSearch.selectReturnCity'), isDisabled: true});
                } else {
                    PopulateReturnCityList(oneWayRoutes, oneWayPickupCity.value);
                }
            }
        }
    }, [oneWayRoutes, PopulateReturnCityList]);

    useEffect(() => {
        // effects from changing one way pick up city selection
        if (Object.keys(oneWayRoutes ?? {}).length > 0 && (oneWayPickupCity.value).length > 0) {
            PopulateReturnCityList(oneWayRoutes, oneWayPickupCity.value);
            if (pickupCityChangeByDD) {
                setOneWayReturnCity({value: '', label: translateService.translate('vehicleSearch.selectReturnCity'), isDisabled: true});
                setPickupCityChangeByDD(false);
            }
        }
    }, [oneWayPickupCity, pickupCityChangeByDD, oneWayRoutes, PopulateReturnCityList]);

    const pickupErrorMessage = React.useMemo(() => {
        switch (pickupError) {
            case "minDateTime":
            case "minTime":
            case "minDate":
                setDisableButton(true);
                return (translateService.translate('vehicleSearch.pickupTimeAtLeast'));

            default:
                setDisableButton(false);
                return null;
        }}, [pickupError]
    );

    const returnErrorMessage = React.useMemo(() => {
            switch (returnError) {
                case "minTime":
                    setDisableButton(true);
                    return (translateService.translate('vehicleSearch.returnTimeAtLeast'));

                case "maxDate":
                    setDisableButton(true);
                    return (translateService.translate('vehicleSearch.returnDateCannotAfter'));

                case "minDate":
                    setDisableButton(true);
                    return (translateService.translate('vehicleSearch.returnDateCannotBefore'));

                case "minDateTime":
                    setDisableButton(true);
                    return (translateService.translate('vehicleSearch.pickupTimeAtLeast'));

                default:
                    setDisableButton(false);
                    return null
            }
        }, [returnError]
    );

    useEffect(() => {
        const searchboxEl = document.getElementById("google-search-location");
        if (!searchboxEl) return;
        searchboxEl.classList.add('searchbox-input');
        const labelInput = document.getElementById('searchbox-label');
        if (!labelInput) return;

        labelInput.style.fontFamily = "'Nunito', Helvetica, Arial, 'Lucida', sans-serif !important";
        if (isLocationEmpty) {
            searchboxEl.style.border = '1px solid #F53232';
            searchboxEl.style.backgroundColor = '#fff8dd';
            labelInput.style.color = '#F53232';
        } else {
            searchboxEl.style.border = '1px solid #ccc';
            searchboxEl.style.backgroundColor = '#fff';
            labelInput.style.color = 'rgba(0, 0, 0, 0.6)';
        }

        const handleClick = () => {
            if (!isLocationEmpty) {
                searchboxEl.style.border = '2px solid #fbb040';
                labelInput.style.color = '#fbb040';
            }
        };
        const handleBlur = () => {
            labelInput.style.color = 'rgba(0, 0, 0, 0.6)';
            if (isLocationEmpty) {
                searchboxEl.style.border = '1px solid #F53232';
                searchboxEl.style.backgroundColor = '#fff8dd';
                labelInput.style.color = '#F53232';
            } else {
                searchboxEl.style.border = '1px solid #ccc';
                searchboxEl.style.backgroundColor = '#fff';
            }
        };

        searchboxEl.addEventListener('click', handleClick);
        searchboxEl.addEventListener('blur', handleBlur);
        return () => {
            searchboxEl.removeEventListener('click', handleClick);
            searchboxEl.removeEventListener('blur', handleBlur);
        };
    }, [isLocationEmpty, mapsReady]);

    const roundToNext15Min = (date: moment.Moment): moment.Moment => {
        const d = date.clone().seconds(0).milliseconds(0);
        const remainder = d.minutes() % 15;
        if (remainder !== 0) {
            d.add(15 - remainder, 'minutes');
        }
        return d;
    };

    const handlePickupDateChange = (p: moment.Moment | null, triggerSearchChange = false) => {
        if (triggerSearchChange) handleSearchChanges();
        if (p === null) return;
        const base = p.isBefore(moment().seconds(0).milliseconds(0)) ? moment() : p;
        const newPickupDate = roundToNext15Min(base);
        setPickupDate(newPickupDate);
        setPickupError(null);

        if (p.isAfter(moment(returnDate).seconds(0).milliseconds(0).subtract(2, "hours"))) {
            const newReturnDate = moment(p).seconds(0).milliseconds(0).add(2, "hours");
            setReturnDate(newReturnDate);
        } else {
            setReturnError(null);
        }
    };

    const handleReturnDateChange = (p: moment.Moment | null, triggerSearchChange = false) => {
        if (p === null) return;
        if (p.isBefore(moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours"))) {
            const newReturnDate = moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours");
            setReturnDate(newReturnDate);
        } else {
            setReturnDate(p);
            setReturnError(null);
        }
        if (triggerSearchChange) handleSearchChanges();
    };

    const handlePickupTimeChange = (p: moment.Moment | null, triggerSearchChange = false) => {
        if (!p) return;
        const newPickupDate = moment(pickupDate).seconds(0).milliseconds(0).set({
            hour: moment(p).get('hour'),
            minute: moment(p).get('minute')
        });
        if (triggerSearchChange) handleSearchChanges();
        if (newPickupDate.isSameOrAfter(moment(returnDate).seconds(0).milliseconds(0).subtract(2, "hours"))) {
            const newReturnDate = moment(newPickupDate).seconds(0).milliseconds(0).add(2, "hours");
            setReturnDate(newReturnDate);
            setPickupDate(newPickupDate);
            setPickupError(null);
        } else {
            setPickupDate(newPickupDate);
            setPickupError(null);
            setReturnError(null);
        }
    };

    const handleReturnTimeChange = (p: moment.Moment | null) => {
        if (p === null) return;
        if (p.isBefore(moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours"))) {
            const newReturnDate = moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours");
            setReturnDate(newReturnDate);
        } else {
            const newReturnDate = moment(returnDate).seconds(0).milliseconds(0).set({
                hour: moment(p).get('hour'),
                minute: moment(p).get('minute')
            });
            setReturnDate(newReturnDate);
            setReturnError(null);
        }
        if (showDefaultResults) {
            changeShowDefaultResults(false);
        }
    };

    const timePickerSlots = { openPickerIcon: MuiDateTimeIcons };

    const timePickerSlotProps = (errorMessage: string | null) => ({
        field: { readOnly: true as const },
        textField: {
            variant: 'outlined' as const,
            error: !!errorMessage,
            helperText: errorMessage
        },
        actionBar: { actions: ["cancel", "accept"] as ("cancel" | "accept")[] },
        inputAdornment: { disablePointerEvents: true },
    });

    const checkDateTimeValues = (startDateToCheck: any, endDateToCheck: any) => {
        // ensure search date time values are in future + min res length is 2h
        let validDateTimes = true;
        if (startDateToCheck < moment()) {
            setPickupError("minDateTime");
            validDateTimes = false;
        }

        if (endDateToCheck < moment()) {
            setReturnError("minDateTime");
            validDateTimes = false;
        } else if (endDateToCheck.isBefore(moment(startDateToCheck).seconds(0).milliseconds(0).add(2, "hours"))) {
            setReturnError("minTime")
            validDateTimes = false;
        }

        return validDateTimes;
    }

    function ResModeSwitch(){
        return (
            <div className={`${styles.onewayCheckboxDiv} ${mobileDevice ? styles.onewayCheckboxDivMobile : ''}`}>
                    <input
                        className={styles.r24OnewayCheckbox}
                           id='r24-ow-input'
                           type={"checkbox"} checked={isOneWayRes}
                           onChange={() => {
                               props.clearResults();
                               !oneWayRes ? sendGa4Event('oneWaySelected') : sendGa4Event('oneWayUnSelected');
                               setIsOneWayRes(!isOneWayRes);
                               changeMapData([]);
                               changeResType(!oneWayRes);
                               if (sessionStorage.getItem('oneWaySearchInit') ?? undefined) {
                                   // allows for default marketing screen to be shown if pickup/return location is empty
                                   sessionStorage.removeItem('oneWaySearchInit')
                               }
                           }}/>
                    <label id='r24-ow-label'
                           className={styles.onewayCheckboxLabel}
                               htmlFor='r24-ow-input'> {translateService.translate('vehicleSearch.returnToDifferentCity')} </label>
                    <CustomTooltip className="tooltip-link" zIndex={100}
                           content={<Translate content={'vehicleSearch.onewayTip'} />}
                           placement={"bottom"}>
                        <img width="23" height="23"
                             className={styles.carFeatureIcon}
                             src={rootUrl+"/assets/icons/new/search/icon-info-oneway-green.svg"} alt="icon-info-oneway-green" loading="lazy"/>
                    </CustomTooltip>
                </div>
        )
    }

    // Runs the normal (address-based) search for a given customer segment. Shared by the two
    // split search buttons (car / van): sets vehicleSegment so it flows to GA4 and persists, and
    // drives the results filter (SearchFilter follows searchParameters.vehicleSegment).
    const submitSearch = (e: React.MouseEvent<HTMLButtonElement>, segment?: VehicleSegment) => {
        e.button === 0 && e.preventDefault(); // prevent double click
        document.getElementById("search-button")?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if ((autoCompleteRef?.current?.value || autoCompleteRef?.current?.value === '')
            && (location?.formatted_address || location?.length)) {
            // note: this is used specifically for the <Autocomplete />, if component for searching changed --> this reset method may need a revisit
            autoCompleteRef.current.value = (location?.formatted_address ?? location); // set field back to location name used for current search
        }

        changeShowingModelsResults(false);
        changeModelId(undefined);
        changeSortingTab('distance');

        if (sessionStorage.getItem('paymentSuccess') ?? undefined) {
            clearSelections();
        }

        const startDate = pickupDate;
        const startTime = pickupDate.clone().format("HH:mm");
        const endDate = returnDate;
        const endTime = returnDate.clone().format("HH:mm");
        if (!checkDateTimeValues(startDate, endDate)) return;

        if (location) {
            // NOTE: this fires changeSearchParameters a second time (setSearchParams below calls it again).
            // ga4SetUserProperties(vehicle_segment) will log twice per click — harmless but worth consolidating later.
            changeSearchParameters({ ...searchParameters, lat: lat, lon: lng, location: location, vehicleSegment: segment });
        }
        if (!hasPopupShown &&
            !loggedIn &&
            (moment().hour() >= 21 || moment().hour() < 8) &&
            (moment().isSame(pickupDate, 'day') || moment(pickupDate).isBefore(moment().add(1, 'day').hour(8).minute(0).second(0).millisecond(0)))) {
            setShowPopup(true);
            setHasPopupShown(true);
        }

        if (!sessionStorage.getItem('searchInit')) {
            sessionStorage.setItem('searchInit', '1');
        }

        if (!props.modifyReservation && location) {
            props.setSearchParams((params: ISearchParameters) => {
                return {
                    ...params,
                    startDate: startDate, startTime: startTime, endDate: endDate, endTime: endTime,
                    lat: lat, lon: lng, distance: 30000,
                    km: 100, showStart: 0, showAmount: 10, discount: params?.discount ?? user?.couponCode,
                    pickupDate: pickupDate, returnDate: returnDate, location: location,
                    carId: !props.modifyReservation ? null : params?.carId,
                    fuelType: props.modifyReservation ? params?.fuelType : undefined,
                    vehicleType: props.modifyReservation ? params?.vehicleType : undefined,
                    crossBorderArea: props.modifyReservation ? params?.crossBorderArea : undefined,
                    transmission: props.modifyReservation ? params?.transmission : undefined,
                    features: props.modifyReservation ? params?.features : undefined, pickupCity: undefined, returnCity: undefined,
                    vehicleSegment: segment,
                    cid: user?.id ?? customerBehaviorData.cid, utm_campaign: customerBehaviorData.utm_campaign,
                    utm_source: customerBehaviorData.utm_source, utm_term: customerBehaviorData.utm_term,
                    utm_medium: customerBehaviorData.utm_medium, utm_content: customerBehaviorData.utm_content,
                    gclid: customerBehaviorData.gclid, cookie: r24CookieVal, userPlaceSearch: userLocationSearchString,
                    // place object → derive fresh; string location (url city / current
                    // location / default) → App.tsx already stored the matching value
                    searchCity: extractCityFromPlace(location) ?? (typeof location === 'string' ? params?.searchCity : undefined)
                };
            });
        } else {
            props.setSearchParams((params: ISearchParameters) => {
                return {
                    ...params,
                    startDate: startDate,
                    startTime: startTime,
                    endDate: endDate,
                    endTime: endTime,
                    pickupDate: pickupDate,
                    returnDate: returnDate,
                    carId: !props.modifyReservation ? null : params.carId,
                    fuelType: undefined, vehicleType: undefined, crossBorderArea: undefined,
                    transmission: undefined, features: undefined, pickupCity: undefined, returnCity: undefined,
                    vehicleSegment: segment,
                    cid: user?.id ?? customerBehaviorData.cid, utm_campaign: customerBehaviorData.utm_campaign,
                    utm_source: customerBehaviorData.utm_source, utm_term: customerBehaviorData.utm_term,
                    utm_medium: customerBehaviorData.utm_medium, utm_content: customerBehaviorData.utm_content,
                    gclid: customerBehaviorData.gclid, cookie: r24CookieVal, userPlaceSearch: userLocationSearchString
                };
            });
        }
    };

    return (
        <>
            {!launcherMode && !showTopTeaser && !props.modifyReservation && !oneWayRes &&
                <TopLevelTeaser rootUrl={rootUrl ?? ''} mobileDevice={mobileDevice ?? true} />}
            {props.modifyReservation ?
                <div className={'text-center'}>
                    <h2 className="past-reservation-title my-3">
                        {translateService.translate('vehicleSearch.services.changeDates')}
                    </h2>
                    {startDateInPast ?
                        <span style={{color: 'red'}}> {translateService.translate('vehicleSearch.pickupTimeAtLeast')} </span>
                        : null }
                </div>
                 : null}
            <Card style={{padding: (oneWayRes && !oneWayDefaultViewed) ? '' : "10px" }}>
                {isOneWayRes ?
                    !oneWayDefaultViewed ?
                    <div style={{ position: 'relative', display: "flex", flexDirection: "column", minHeight: '100vh' }}>
                        <div className={styles.oneWayInfoSectionWithBg}
                            style={{backgroundImage: `url("https://vehicles-cdn.24rent.fi/modern-car-driving1920x900.jpg")`}}/>
                        <div style={{ zIndex: 1 }}>
                            <TopLevelTeaser rootUrl={rootUrl ?? ''} mobileDevice={mobileDevice ?? true} />
                            <form style={{ backgroundColor: "white", padding: 20, width: mobileDevice ? "90%" : "60%", margin: "10px auto", zIndex: 1000, borderRadius: 4 }}>
                                    {!props.modifyReservation &&
                                        <div className={styles.onewaySearchDropdownContainer}>
                                            <div className={styles.onewayPickupDropdown}>
                                                <Select options={oneWaySelectOptionsPickup}
                                                        onChange={(e: { value: string; label: string; isDisabled: boolean } | null) => {
                                                            handleOneWayCityChange(e, 'pickup')}}
                                                        value={oneWayPickupCity.value === '' ? {value: '', label: translateService.translate('vehicleSearch.selectPickupCity'), isDisabled: true} : oneWayPickupCity}
                                                        isOptionDisabled={(pickUpOptions: any) => pickUpOptions.isDisabled}
                                                        theme={(theme: Theme) => ({...theme, borderRadius: 4, colors: {...theme.colors, primary25: '#ddd', primary: '#fbb040',}
                                                        })}/>
                                            </div>
                                        </div>}
                                    { enableOneWay && !props.modifyReservation ? <ResModeSwitch /> : null }
                                    {!props.modifyReservation &&
                                        <div className={styles.onewaySearchDropdownContainer}>
                                            <div className={styles.onewayReturnDropdown}>
                                                <Select options={oneWaySelectOptionsReturn} isDisabled={(oneWayPickupCity.value).length <= 0}
                                                        onChange={(e: { value: string; label: string; isDisabled: boolean } | null) => {handleOneWayCityChange(e, 'return')}}
                                                        value={oneWayReturnCity.value === '' ? {value: '', label: translateService.translate('vehicleSearch.selectReturnCity'), isDisabled: true} : oneWayReturnCity}
                                                        isOptionDisabled={(returnOptions: any) => returnOptions.isDisabled} theme={(theme: Theme) => ({
                                                    ...theme, borderRadius: 4,
                                                    colors: {...theme.colors, primary25: '#ddd', primary: '#fbb040',}
                                                })}/>
                                            </div>
                                        </div>}
                                    <div className='row mx-0 px-0'>
                                        <ThemeProvider theme={yellowTheme}>
                                            <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={language} localeText={language === 'en' ? enUS.components.MuiLocalizationProvider.defaultProps.localeText : fiFI.components.MuiLocalizationProvider.defaultProps.localeText}>
                                                { mobileDevice ?
                                                    <>
                                                        <div className={styles.dateTimeInputsRow}>
                                                            <div className={`${!props.modifyReservation ? `dateInputWrapper position-relative pl-0 ${mobileDevice ? styles.mobileDateInput : ''}`
                                                                : `position-relative px-0 ${mobileDevice ? styles.mobileDateInput : ''}`}`}>
                                                                {!props.modifyReservation &&
                                                                    <img width="20" height="20" className='dateIcons' onClick={() => {setPickupDatePickerOpen(true)}} src={rootUrl+'/assets/icons/new/search/input-icon-pickup-date (1).svg'} alt="icon-pickup-vehicle.svg" loading="lazy"/>}
                                                                <MobileDatePicker
                                                                    disabled={false}
                                                                    open={isPickupDatePickerOpen}
                                                                    onAccept={() => {
                                                                        setPickupDatePickerOpen(false);
                                                                    }}
                                                                    onOpen={() => {setPickupDatePickerOpen(true)}}
                                                                    onClose={() => {
                                                                        setPickupDatePickerOpen(false);
                                                                    }}
                                                                    className={`vehicleSearch-mobileDateInputs ${!props.modifyReservation ? 'dateInputsOneWay' : ''}`}
                                                                    closeOnSelect
                                                                    label={translateService.translate('vehicleSearch.pickupDate')}
                                                                    disablePast
                                                                    format="DD.MM.YYYY"
                                                                    value={pickupDate}
                                                                    onChange={(p) => handlePickupDateChange(p)}
                                                                    slotProps={{
                                                                        textField: {
                                                                            variant: 'outlined',
                                                                            error: !!pickupErrorMessage,
                                                                            helperText: pickupErrorMessage
                                                                        }
                                                                    }}
                                                                    onError={(error: any) => setPickupError(error)}
                                                                />
                                                            </div>
                                                            <div className={`${!props.modifyReservation ? `dateInputWrapper position-relative px-0 ${mobileDevice ? styles.mobileDateInput : ''}`
                                                                : `position-relative px-0 ${mobileDevice ? styles.mobileDateInput : ''}`}`}>
                                                                {!props.modifyReservation &&  <img width="20" height="20" className='dateIcons' onClick={() => {setPickupTimePickerOpen(true)}} src={rootUrl+'/assets/icons/new/search/input-icon-pickup-time (2).svg'} alt="icon-pickup-vehicle.svg" loading="lazy"/>}
                                                                <div onClick={() => {if (!isPickupTimePickerOpen) {setPickupTimePickerOpen(true)}}}>
                                                                    <DesktopTimePicker
                                                                        closeOnSelect={true}
                                                                        open={isPickupTimePickerOpen}
                                                                        onAccept={() => {setPickupTimePickerOpen(false)}}
                                                                        onOpen={() => {setPickupTimePickerOpen(true)}}
                                                                        onClose={() => {setPickupTimePickerOpen(false)}}
                                                                        label={translateService.translate('vehicleSearch.pickupTime')}
                                                                        format={"HH:mm"}
                                                                        minutesStep={15}
                                                                        skipDisabled
                                                                        minTime={moment().isSame(pickupDate, 'day') ? moment().seconds(0).milliseconds(0) : moment()}
                                                                        value={moment(pickupDate).seconds(0).milliseconds(0)}
                                                                        className={`vehicleSearch-mobileDateInputs ${!props.modifyReservation ? 'dateInputsOneWay' : ''}`}
                                                                        disabled={false}
                                                                        onChange={(p) => handlePickupTimeChange(p)}
                                                                        slots={timePickerSlots}
                                                                        slotProps={timePickerSlotProps(pickupErrorMessage)}
                                                                        disableIgnoringDatePartForTimeValidation
                                                                        ampm={false}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className={styles.dateTimeInputsRow}>
                                                            <div className={`${!props.modifyReservation ? `dateInputWrapper position-relative pl-0 ${mobileDevice ? styles.mobileDateInput : ''}`
                                                                : `position-relative px-0 ${mobileDevice ? styles.mobileDateInput : ''}`}`}>
                                                                {!props.modifyReservation && <img width="20" height="20" className='dateIcons' onClick={() => {setReturnDatePickerOpen(true)}} src={rootUrl+'/assets/icons/new/search/input-icon-return-date (1).svg'} alt="icon-return-vehicle.svg" loading="lazy"/>}
                                                                <MobileDatePicker
                                                                    disabled={false}
                                                                    open={isReturnDatePickerOpen}
                                                                    onOpen={() =>{
                                                                        setReturnDatePickerOpen(true);
                                                                    }}
                                                                    onAccept={() => {setReturnDatePickerOpen(false)}}
                                                                    onClose={() => {setReturnDatePickerOpen(false)}}
                                                                    closeOnSelect={true}
                                                                    disablePast
                                                                    format="DD.MM.YYYY"
                                                                    label={translateService.translate('vehicleSearch.returnDate')}
                                                                    className={`vehicleSearch-mobileDateInputs ${!props.modifyReservation ? 'dateInputsOneWay' : ''}`}
                                                                    minDate={moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours")}
                                                                    maxDate={moment(pickupDate).seconds(0).milliseconds(0).add(60, "days")}
                                                                    value={returnDate}
                                                                    onChange={(p) => handleReturnDateChange(p)}
                                                                    slotProps={{
                                                                        textField: {
                                                                            variant: 'outlined',
                                                                            error: !!returnErrorMessage,
                                                                            helperText: returnErrorMessage
                                                                        }
                                                                    }}
                                                                    onError={(error) => setReturnError(error)}
                                                                />
                                                            </div>
                                                            <div className={`${!props.modifyReservation ? `dateInputWrapper position-relative px-0 ${mobileDevice ? styles.mobileDateInput : ''}`
                                                                : `position-relative px-0 ${mobileDevice ? styles.mobileDateInput : ''}`}`}>
                                                                {!props.modifyReservation && <img width="20" height="20" className='dateIcons' onClick={() => {setReturnTimePickerOpen(true)}} src={rootUrl+'/assets/icons/new/search/input-icon-return-time (1).svg'} alt="icon-return-vehicle.svg" loading="lazy"/>}
                                                                <div onClick={() => {if (!isReturnTimePickerOpen) {setReturnTimePickerOpen(true)}}}>
                                                                    <DesktopTimePicker
                                                                        onOpen={() => {setReturnTimePickerOpen(true)}}
                                                                        open={isReturnTimePickerOpen}
                                                                        onAccept={() => {
                                                                            setReturnDatePickerOpen(false);
                                                                            setReturnTimePickerOpen(false);
                                                                        }}
                                                                        onClose={() => {setReturnTimePickerOpen(false)}}
                                                                        closeOnSelect={true}
                                                                        label={translateService.translate('vehicleSearch.returnTime')}
                                                                        format={"HH:mm"}
                                                                        minutesStep={15}
                                                                        skipDisabled
                                                                        minTime={moment(pickupDate).isSame(returnDate, 'day') ? moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours") : moment()}
                                                                        value={moment(returnDate).seconds(0).milliseconds(0)}
                                                                        className={`vehicleSearch-mobileDateInputs ${!props.modifyReservation ? 'dateInputsOneWay' : ''}`}
                                                                        disabled={false}
                                                                        onChange={(p) => handleReturnTimeChange(p)}
                                                                        slots={timePickerSlots}
                                                                        slotProps={timePickerSlotProps(returnErrorMessage)}
                                                                        disableIgnoringDatePartForTimeValidation
                                                                        ampm={false}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </>
                                                    : // DESKTOP MODE
                                                    <>
                                                        <div className={`${!props.modifyReservation ? 'dateInputWrapper col-12 position-relative col-md-6 col-lg-6 pl-0' : 'col-12 position-relative px-0'}`}>
                                                            {!props.modifyReservation &&
                                                                <img width="20" height="20" className='dateIcons' onClick={() => {setPickupDatePickerOpen(true)}} src={rootUrl+'/assets/icons/new/search/input-icon-pickup-date (1).svg'} alt="icon-pickup-vehicle.svg" loading="lazy"/>}
                                                                <div onClick={() => {if (!isPickupDatePickerOpen) {setPickupDatePickerOpen(true)}}}>
                                                                    <DatePicker
                                                                        disabled={false}
                                                                        open={isPickupDatePickerOpen}
                                                                        onAccept={() => {setPickupDatePickerOpen(false)}}
                                                                        onOpen={() => {setPickupDatePickerOpen(true)}}
                                                                        onClose={() => {setPickupDatePickerOpen(false)}}
                                                                        className={`vehicleSearch-inputs ${!props.modifyReservation ? 'dateInputsOneWay' : ''}`}
                                                                        closeOnSelect={false}
                                                                        label={translateService.translate('vehicleSearch.pickupDate')}
                                                                        disablePast
                                                                        format="DD.MM.YYYY"
                                                                        value={pickupDate}
                                                                        onChange={(p) => handlePickupDateChange(p)}
                                                                        slots={{openPickerIcon: MuiDateTimeIcons}}
                                                                        slotProps={{
                                                                            textField: {
                                                                                variant: 'outlined',
                                                                                error: !!pickupErrorMessage,
                                                                                helperText: pickupErrorMessage
                                                                            },
                                                                            field: {readOnly: true},
                                                                            actionBar: {
                                                                                actions: ["cancel", "accept"],
                                                                            },
                                                                            inputAdornment: {
                                                                                disablePointerEvents: true
                                                                            },
                                                                            popper: {
                                                                                popperOptions: {
                                                                                    placement: !props.modifyReservation ? "bottom" : "auto"
                                                                                }
                                                                            }
                                                                        }}
                                                                        onError={(error: any) => setPickupError(error)}
                                                                    />
                                                                </div>
                                                        </div>
                                                        <div className={`${!props.modifyReservation ? 'dateInputWrapper col-12 position-relative col-md-6 col-lg-6  px-0' : 'col-12 position-relative px-0'}`}>
                                                            {!props.modifyReservation &&  <img width="20" height="20" className='dateIcons' onClick={() => {setPickupTimePickerOpen(true)}} src={rootUrl+'/assets/icons/new/search/input-icon-pickup-time (2).svg'} alt="icon-pickup-vehicle.svg" loading="lazy"/>}
                                                                <div onClick={() => {if (!isPickupTimePickerOpen) {setPickupTimePickerOpen(true)}}}>
                                                                    <DesktopTimePicker
                                                                        closeOnSelect={true}
                                                                        open={isPickupTimePickerOpen}
                                                                        onAccept={() => {setPickupTimePickerOpen(false)}}
                                                                        onOpen={() => {
                                                                            setPickupTimePickerOpen(true);
                                                                        }}
                                                                        onClose={() => {setPickupTimePickerOpen(false);}}
                                                                        label={translateService.translate('vehicleSearch.pickupTime')}
                                                                        format={"HH:mm"}
                                                                        minutesStep={15}
                                                                        skipDisabled
                                                                        minTime={moment().isSame(pickupDate, 'day') ? moment().seconds(0).milliseconds(0) : moment()}
                                                                        value={moment(pickupDate).seconds(0).milliseconds(0)}
                                                                        className={`vehicleSearch-inputs ${!props.modifyReservation ? 'dateInputsOneWay' : ''}`}
                                                                        disabled={false}
                                                                        onChange={(p) => handlePickupTimeChange(p)}
                                                                        slots={timePickerSlots}
                                                                        slotProps={timePickerSlotProps(pickupErrorMessage)}
                                                                        disableIgnoringDatePartForTimeValidation
                                                                        ampm={false}
                                                                    />
                                                                </div>
                                                        </div>
                                                        <div className={`${!props.modifyReservation ? 'dateInputWrapper col-12 position-relative col-md-6 col-lg-6 pl-0' : 'col-12 position-relative px-0'}`}>
                                                            {!props.modifyReservation && <img width="20" height="20" className='dateIcons' onClick={() => {setReturnDatePickerOpen(true)}} src={rootUrl+'/assets/icons/new/search/input-icon-return-date (1).svg'} alt="icon-return-vehicle.svg" loading="lazy"/>}
                                                                <div onClick={() => {if (!isReturnDatePickerOpen) {setReturnDatePickerOpen(true)}}}>
                                                                    <DatePicker
                                                                        disabled={false}
                                                                        open={isReturnDatePickerOpen}
                                                                        onOpen={() =>{
                                                                            setReturnDatePickerOpen(true);
                                                                        }}
                                                                        onAccept={() => {setReturnDatePickerOpen(false)}}
                                                                        onClose={() => {setReturnDatePickerOpen(false)}}
                                                                        closeOnSelect={false}
                                                                        disablePast
                                                                        format="DD.MM.YYYY"
                                                                        label={translateService.translate('vehicleSearch.returnDate')}
                                                                        className={`vehicleSearch-inputs ${!props.modifyReservation ? 'dateInputsOneWay' : ''}`}
                                                                        minDate={moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours")}
                                                                        maxDate={moment(pickupDate).seconds(0).milliseconds(0).add(60, "days")}
                                                                        value={returnDate}
                                                                        onChange={(p) => handleReturnDateChange(p)}
                                                                        slots={{openPickerIcon: MuiDateTimeIcons}}
                                                                        slotProps={{
                                                                            textField: {
                                                                                variant: 'outlined',
                                                                                error: !!returnErrorMessage,
                                                                                helperText: returnErrorMessage
                                                                            },
                                                                            field: {readOnly: true},
                                                                            actionBar: {
                                                                                actions: ["cancel", "accept"],
                                                                            },
                                                                            popper: {
                                                                                popperOptions: {
                                                                                    placement: !props.modifyReservation ? "bottom" : "auto"
                                                                                }
                                                                            },
                                                                            inputAdornment: {
                                                                                disablePointerEvents: true
                                                                            },
                                                                        }}
                                                                        onError={(error) => setReturnError(error)}
                                                                    />
                                                                </div>
                                                        </div>
                                                        <div className={`${!props.modifyReservation ? 'dateInputWrapper col-12 position-relative col-md-6 col-lg-6  px-0' : 'col-12 position-relative px-0'}`}>
                                                            {!props.modifyReservation && <img width="20" height="20" className='dateIcons' onClick={() => {setReturnTimePickerOpen(true)}} src={rootUrl+'/assets/icons/new/search/input-icon-return-time (1).svg'} alt="icon-return-vehicle.svg" loading="lazy"/>}
                                                                <div onClick={() => {if (!isReturnTimePickerOpen) {setReturnTimePickerOpen(true)}}}>
                                                                    <DesktopTimePicker
                                                                        onOpen={() => {
                                                                            setReturnTimePickerOpen(true);
                                                                        }}
                                                                        open={isReturnTimePickerOpen}
                                                                        onAccept={() => {
                                                                            setReturnDatePickerOpen(false);
                                                                            setReturnTimePickerOpen(false);
                                                                        }}
                                                                        onClose={() => {setReturnTimePickerOpen(false)}}
                                                                        closeOnSelect={true}
                                                                        label={translateService.translate('vehicleSearch.returnTime')}
                                                                        format={"HH:mm"}
                                                                        minutesStep={15}
                                                                        skipDisabled
                                                                        minTime={moment(pickupDate).isSame(returnDate, 'day') ? moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours") : moment()}
                                                                        value={moment(returnDate).seconds(0).milliseconds(0)}
                                                                        className={`vehicleSearch-inputs ${!props.modifyReservation ? 'dateInputsOneWay' : ''}`}
                                                                        disabled={false}
                                                                        onChange={(p) => handleReturnTimeChange(p)}
                                                                        slots={timePickerSlots}
                                                                        slotProps={timePickerSlotProps(returnErrorMessage)}
                                                                        disableIgnoringDatePartForTimeValidation
                                                                        ampm={false}
                                                                    />
                                                                </div>
                                                        </div>
                                                    </>
                                                }
                                            </LocalizationProvider>
                                        </ThemeProvider>
                                    </div>
                                    <div className={styles.oneWaySearchButtonContainer}>
                                        <button
                                            id='search-button'
                                            type="submit"
                                            disabled={(disableButton || (props.modifyReservation ?
                                                (searchParameters?.returnCity ? searchParameters?.returnCity.length < 1 : true) : (oneWayReturnCity.value).length === 0))}
                                            className="btn btn-primary btn-outline-primary btn-cta w-50 font-weight-bold"
                                            style={{marginTop: "10px"}}
                                            onClick={(e) => {
                                                e.button === 0 && e.preventDefault(); // prevent double click
                                                document.getElementById("search-button")?.scrollIntoView({
                                                    behavior: 'smooth',
                                                    block: 'center'
                                                });
                                                handleSearchChanges();
                                                const startDate = pickupDate;
                                                const startTime = pickupDate.clone().format("HH:mm");
                                                const endDate = returnDate;
                                                const endTime = returnDate.clone().format("HH:mm");

                                                if (!checkDateTimeValues(startDate, endDate)) return;
                                                if (!sessionStorage.getItem('searchInit')) {
                                                    sessionStorage.setItem('searchInit', '1');
                                                }

                                                if (!props.modifyReservation && (oneWayPickupCity.value).length && (oneWayReturnCity.value).length) {
                                                    props.setSearchParams((params: ISearchParameters) => {
                                                        return {
                                                            ...params,
                                                            startDate: startDate,
                                                            startTime: startTime,
                                                            endDate: endDate,
                                                            endTime: endTime,
                                                            km: 100,
                                                            showStart: 0,
                                                            showAmount: 10,
                                                            pickupDate: pickupDate,
                                                            returnDate: returnDate,
                                                            pickupCity: oneWayPickupCity.value,
                                                            returnCity: oneWayReturnCity.value,
                                                            carId: !props.modifyReservation ? null : params.carId,
                                                            cid: user?.id ?? customerBehaviorData.cid,
                                                            utm_campaign: customerBehaviorData.utm_campaign,
                                                            utm_source: customerBehaviorData.utm_source,
                                                            utm_term: customerBehaviorData.utm_term,
                                                            utm_medium: customerBehaviorData.utm_medium,
                                                            utm_content: customerBehaviorData.utm_content,
                                                            gclid: customerBehaviorData.gclid,
                                                            cookie: r24CookieVal
                                                        };
                                                    });
                                                } else {
                                                    props.setSearchParams((params: ISearchParameters) => {
                                                        return {
                                                            ...params,
                                                            startDate: startDate,
                                                            startTime: startTime,
                                                            endDate: endDate,
                                                            endTime: endTime,
                                                            pickupDate: pickupDate,
                                                            returnDate: returnDate,
                                                            carId: !props.modifyReservation ? null : params.carId,
                                                            cid: user?.id ?? customerBehaviorData.cid,
                                                            utm_campaign: customerBehaviorData.utm_campaign,
                                                            utm_source: customerBehaviorData.utm_source,
                                                            utm_term: customerBehaviorData.utm_term,
                                                            utm_medium: customerBehaviorData.utm_medium,
                                                            utm_content: customerBehaviorData.utm_content,
                                                            gclid: customerBehaviorData.gclid,
                                                            cookie: r24CookieVal
                                                        };
                                                    });
                                                }

                                                if (!(sessionStorage.getItem('oneWaySearchInit') ?? undefined)) {
                                                    // indicator for default screen visibility
                                                    sessionStorage.setItem('oneWaySearchInit', 'true')
                                                }
                                            }}>
                                            {!props.modifyReservation ?
                                                (oneWayPickupCity.value === '' || oneWayReturnCity.value === '') ?
                                                    (oneWayPickupCity.value === '' ? translateService.translate('vehicleSearch.selectPickupCity')
                                                        : translateService.translate('vehicleSearch.selectReturnCity'))
                                                    : translateService.translate('vehicleSearch.find')
                                                : translateService.translate('vehicleSearch1.checkAvailability')}
                                        </button>
                                    </div>
                                    <div style={{textAlign: mobileDevice ? 'right' : 'left', margin: '10px 10px 0 10px'}}>
                                        {!props.modifyReservation ?
                                            (landBotReady ?
                                                    <LandBot
                                                        botId={owLandbotConfig[language === 'fi' ? 'fi' : 'en'].id}
                                                        baseUrl={owLandbotConfig[language === 'fi' ? 'fi' : 'en'].baseUrl ?? undefined}
                                                        buttonContent={
                                                            <span style={{textDecoration: 'underline', cursor: 'pointer', color: '#fdb022'}}>
                                                                {translateService.translate('vehicleSearch.questions2')}
                                                            </span>}
                                                        language={language}
                                                        params={{
                                                            phase: "",
                                                            msg_token: "-1",
                                                            customer_id: -1,
                                                            is_leasing: "false",
                                                            requester: "",
                                                            reservation_id: -1,
                                                            vehicle_id: undefined,
                                                            model_id: undefined,
                                                        }}
                                                    /> :
                                                    <CustomTooltip
                                                        className="tooltip-link"
                                                        zIndex={100}
                                                        content={<Translate content={'vehicleSearch.onewayDoesNotWork'}/>}
                                                        placement={"bottom"}>
                                                        <span style={{textDecoration: 'underline', cursor: 'pointer'}}>
                                                            {translateService.translate('vehicleSearch.questions')}
                                                        </span>
                                                    </CustomTooltip>
                                            ) : null
                                        }
                                    </div>
                                </form>
                            <OneWayFirstView2 rootUrl={rootUrl ?? ''} onQuickSearch={handleQuickSearch} />
                        </div>
                    </div>
                        :
                        <form>

                            {!props.modifyReservation &&
                                <div className={styles.onewaySearchDropdownContainer}>
                                    <div className={styles.onewayPickupDropdown}>
                                        <Select options={oneWaySelectOptionsPickup}
                                                onChange={(e: { value: string; label: string; isDisabled: boolean } | null) => {
                                                    handleOneWayCityChange(e, 'pickup')}}
                                                value={oneWayPickupCity.value === '' ? {value: '', label: translateService.translate('vehicleSearch.selectPickupCity'), isDisabled: true} : oneWayPickupCity}
                                                isOptionDisabled={(pickUpOptions: any) => pickUpOptions.isDisabled}
                                                theme={(theme: Theme) => ({...theme, borderRadius: 4, colors: {...theme.colors, primary25: '#ddd', primary: '#fbb040',}
                                                })}/>
                                    </div>
                                </div>}
                            { enableOneWay && !props.modifyReservation ? <ResModeSwitch /> : null }
                            {!props.modifyReservation &&
                                <div className={styles.onewaySearchDropdownContainer}>
                                    <div className={styles.onewayReturnDropdown}>
                                        <Select options={oneWaySelectOptionsReturn} isDisabled={(oneWayPickupCity.value).length <= 0}
                                                onChange={(e: { value: string; label: string; isDisabled: boolean } | null) => {handleOneWayCityChange(e, 'return')}}
                                                value={oneWayReturnCity.value === '' ? {value: '', label: translateService.translate('vehicleSearch.selectReturnCity'), isDisabled: true} : oneWayReturnCity}
                                                isOptionDisabled={(returnOptions: any) => returnOptions.isDisabled} theme={(theme: Theme) => ({
                                            ...theme, borderRadius: 4,
                                            colors: {...theme.colors, primary25: '#ddd', primary: '#fbb040',}
                                        })}/>
                                    </div>
                                </div>}
                            <div className='row mx-0 px-0'>
                                <ThemeProvider theme={yellowTheme}>
                                    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={language} localeText={language === 'en' ? enUS.components.MuiLocalizationProvider.defaultProps.localeText : fiFI.components.MuiLocalizationProvider.defaultProps.localeText}>
                                        { mobileDevice ?
                                            <>
                                                <div className={styles.dateTimeInputsRow}>
                                                    <div className={`${!props.modifyReservation ? `dateInputWrapper position-relative pl-0 ${mobileDevice ? styles.mobileDateInput : ''}`
                                                        : `position-relative px-0 ${mobileDevice ? styles.mobileDateInput : ''}`}`}>
                                                        {!props.modifyReservation &&
                                                            <img width="20" height="20" className='dateIcons' onClick={() => {setPickupDatePickerOpen(true)}} src={rootUrl+'/assets/icons/new/search/input-icon-pickup-date (1).svg'} alt="icon-pickup-vehicle.svg" loading="lazy"/>}
                                                        <MobileDatePicker
                                                            disabled={false}
                                                            open={isPickupDatePickerOpen}
                                                            onAccept={() => {
                                                                setPickupDatePickerOpen(false);
                                                            }}
                                                            onOpen={() => {setPickupDatePickerOpen(true)}}
                                                            onClose={() => {
                                                                setPickupDatePickerOpen(false);
                                                            }}
                                                            className={`vehicleSearch-mobileDateInputs ${!props.modifyReservation ? 'dateInputsOneWay' : ''}`}
                                                            closeOnSelect
                                                            label={translateService.translate('vehicleSearch.pickupDate')}
                                                            disablePast
                                                            format="DD.MM.YYYY"
                                                            value={pickupDate}
                                                            onChange={(p) => handlePickupDateChange(p)}
                                                            slotProps={{
                                                                textField: {
                                                                    variant: 'outlined',
                                                                    error: !!pickupErrorMessage,
                                                                    helperText: pickupErrorMessage
                                                                }
                                                            }}
                                                            onError={(error: any) => setPickupError(error)}
                                                        />
                                                    </div>
                                                    <div className={`${!props.modifyReservation ? `dateInputWrapper position-relative px-0 ${mobileDevice ? styles.mobileDateInput : ''}`
                                                        : `position-relative px-0 ${mobileDevice ? styles.mobileDateInput : ''}`}`}>
                                                        {!props.modifyReservation &&  <img width="20" height="20" className='dateIcons' onClick={() => {setPickupTimePickerOpen(true)}} src={rootUrl+'/assets/icons/new/search/input-icon-pickup-time (2).svg'} alt="icon-pickup-vehicle.svg" loading="lazy"/>}
                                                        <div onClick={() => {if (!isPickupTimePickerOpen) {setPickupTimePickerOpen(true)}}}>
                                                            <DesktopTimePicker
                                                                closeOnSelect={true}
                                                                open={isPickupTimePickerOpen}
                                                                onAccept={() => {setPickupTimePickerOpen(false)}}
                                                                onOpen={() => {setPickupTimePickerOpen(true)}}
                                                                onClose={() => {setPickupTimePickerOpen(false)}}
                                                                label={translateService.translate('vehicleSearch.pickupTime')}
                                                                format={"HH:mm"}
                                                                minutesStep={15}
                                                                skipDisabled
                                                                minTime={moment().isSame(pickupDate, 'day') ? moment().seconds(0).milliseconds(0) : moment()}
                                                                value={moment(pickupDate).seconds(0).milliseconds(0)}
                                                                className={`vehicleSearch-mobileDateInputs ${!props.modifyReservation ? 'dateInputsOneWay' : ''}`}
                                                                disabled={false}
                                                                onChange={(p) => handlePickupTimeChange(p)}
                                                                slots={timePickerSlots}
                                                                slotProps={timePickerSlotProps(pickupErrorMessage)}
                                                                disableIgnoringDatePartForTimeValidation
                                                                ampm={false}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className={styles.dateTimeInputsRow}>
                                                    <div className={`${!props.modifyReservation ? `dateInputWrapper position-relative pl-0 ${mobileDevice ? styles.mobileDateInput : ''}`
                                                        : `position-relative px-0 ${mobileDevice ? styles.mobileDateInput : ''}`}`}>
                                                        {!props.modifyReservation && <img width="20" height="20" className='dateIcons' onClick={() => {setReturnDatePickerOpen(true)}} src={rootUrl+'/assets/icons/new/search/input-icon-return-date (1).svg'} alt="icon-return-vehicle.svg" loading="lazy"/>}
                                                        <MobileDatePicker
                                                            disabled={false}
                                                            open={isReturnDatePickerOpen}
                                                            onOpen={() =>{
                                                                setReturnDatePickerOpen(true);
                                                            }}
                                                            onAccept={() => {setReturnDatePickerOpen(false)}}
                                                            onClose={() => {setReturnDatePickerOpen(false)}}
                                                            closeOnSelect={true}
                                                            disablePast
                                                            format="DD.MM.YYYY"
                                                            label={translateService.translate('vehicleSearch.returnDate')}
                                                            className={`vehicleSearch-mobileDateInputs ${!props.modifyReservation ? 'dateInputsOneWay' : ''}`}
                                                            minDate={moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours")}
                                                            maxDate={moment(pickupDate).seconds(0).milliseconds(0).add(60, "days")}
                                                            value={returnDate}
                                                            onChange={(p) => handleReturnDateChange(p)}
                                                            slotProps={{
                                                                textField: {
                                                                    variant: 'outlined',
                                                                    error: !!returnErrorMessage,
                                                                    helperText: returnErrorMessage
                                                                }
                                                            }}
                                                            onError={(error) => setReturnError(error)}
                                                        />
                                                    </div>
                                                    <div className={`${!props.modifyReservation ? `dateInputWrapper position-relative px-0 ${mobileDevice ? styles.mobileDateInput : ''}`
                                                        : `position-relative px-0 ${mobileDevice ? styles.mobileDateInput : ''}`}`}>
                                                        {!props.modifyReservation && <img width="20" height="20" className='dateIcons' onClick={() => {setReturnTimePickerOpen(true)}} src={rootUrl+'/assets/icons/new/search/input-icon-return-time (1).svg'} alt="icon-return-vehicle.svg" loading="lazy"/>}
                                                        <div onClick={() => {if (!isReturnTimePickerOpen) {setReturnTimePickerOpen(true)}}}>
                                                            <DesktopTimePicker
                                                                onOpen={() => {setReturnTimePickerOpen(true)}}
                                                                open={isReturnTimePickerOpen}
                                                                onAccept={() => {
                                                                    setReturnDatePickerOpen(false);
                                                                    setReturnTimePickerOpen(false);
                                                                }}
                                                                onClose={() => {setReturnTimePickerOpen(false)}}
                                                                closeOnSelect={true}
                                                                label={translateService.translate('vehicleSearch.returnTime')}
                                                                format={"HH:mm"}
                                                                minutesStep={15}
                                                                skipDisabled
                                                                minTime={moment(pickupDate).isSame(returnDate, 'day') ? moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours") : moment()}
                                                                value={moment(returnDate).seconds(0).milliseconds(0)}
                                                                className={`vehicleSearch-mobileDateInputs ${!props.modifyReservation ? 'dateInputsOneWay' : ''}`}
                                                                disabled={false}
                                                                onChange={(p) => handleReturnTimeChange(p)}
                                                                slots={timePickerSlots}
                                                                slotProps={timePickerSlotProps(returnErrorMessage)}
                                                                disableIgnoringDatePartForTimeValidation
                                                                ampm={false}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                            : // DESKTOP MODE
                                            <>
                                                <div className={`${!props.modifyReservation ? 'dateInputWrapper col-12 position-relative col-md-6 col-lg-6 pl-0' : 'col-12 position-relative px-0'}`}>
                                                    {!props.modifyReservation &&
                                                        <img width="20" height="20" className='dateIcons' onClick={() => {setPickupDatePickerOpen(true)}} src={rootUrl+'/assets/icons/new/search/input-icon-pickup-date (1).svg'} alt="icon-pickup-vehicle.svg" loading="lazy"/>}
                                                        <div onClick={() => {if (!isPickupDatePickerOpen) {setPickupDatePickerOpen(true)}}}>
                                                            <DatePicker
                                                                disabled={false}
                                                                open={isPickupDatePickerOpen}
                                                                onAccept={() => {setPickupDatePickerOpen(false)}}
                                                                onOpen={() => {setPickupDatePickerOpen(true)}}
                                                                onClose={() => {setPickupDatePickerOpen(false)}}
                                                                className={`vehicleSearch-inputs ${!props.modifyReservation ? 'dateInputsOneWay' : ''}`}
                                                                closeOnSelect={false}
                                                                label={translateService.translate('vehicleSearch.pickupDate')}
                                                                disablePast
                                                                format="DD.MM.YYYY"
                                                                value={pickupDate}
                                                                onChange={(p) => handlePickupDateChange(p)}
                                                                slots={{openPickerIcon: MuiDateTimeIcons}}
                                                                slotProps={{
                                                                    textField: {
                                                                        variant: 'outlined',
                                                                        error: !!pickupErrorMessage,
                                                                        helperText: pickupErrorMessage
                                                                    },
                                                                    field: {readOnly: true},
                                                                    actionBar: {
                                                                        actions: ["cancel", "accept"],
                                                                    },
                                                                    inputAdornment: {
                                                                        disablePointerEvents: true
                                                                    },
                                                                    popper: {
                                                                        popperOptions: {
                                                                            placement: !props.modifyReservation ? "bottom" : "auto"
                                                                        }
                                                                    }
                                                                }}
                                                                onError={(error: any) => setPickupError(error)}
                                                            />
                                                        </div>
                                                </div>
                                                <div className={`${!props.modifyReservation ? 'dateInputWrapper col-12 position-relative col-md-6 col-lg-6  px-0' : 'col-12 position-relative px-0'}`}>
                                                    {!props.modifyReservation &&  <img width="20" height="20" className='dateIcons' onClick={() => {setPickupTimePickerOpen(true)}} src={rootUrl+'/assets/icons/new/search/input-icon-pickup-time (2).svg'} alt="icon-pickup-vehicle.svg" loading="lazy"/>}
                                                        <div onClick={() => {if (!isPickupTimePickerOpen) {setPickupTimePickerOpen(true)}}}>
                                                            <DesktopTimePicker
                                                                closeOnSelect={true}
                                                                open={isPickupTimePickerOpen}
                                                                onAccept={() => {setPickupTimePickerOpen(false)}}
                                                                onOpen={() => {
                                                                    setPickupTimePickerOpen(true);
                                                                }}
                                                                onClose={() => {setPickupTimePickerOpen(false);}}
                                                                label={translateService.translate('vehicleSearch.pickupTime')}
                                                                format={"HH:mm"}
                                                                minutesStep={15}
                                                                skipDisabled
                                                                minTime={moment().isSame(pickupDate, 'day') ? moment().seconds(0).milliseconds(0) : moment()}
                                                                value={moment(pickupDate).seconds(0).milliseconds(0)}
                                                                className={`vehicleSearch-inputs ${!props.modifyReservation ? 'dateInputsOneWay' : ''}`}
                                                                disabled={false}
                                                                onChange={(p) => handlePickupTimeChange(p)}
                                                                slots={timePickerSlots}
                                                                slotProps={timePickerSlotProps(pickupErrorMessage)}
                                                                disableIgnoringDatePartForTimeValidation
                                                                ampm={false}
                                                            />
                                                        </div>
                                                </div>
                                                <div className={`${!props.modifyReservation ? 'dateInputWrapper col-12 position-relative col-md-6 col-lg-6 pl-0' : 'col-12 position-relative px-0'}`}>
                                                    {!props.modifyReservation && <img width="20" height="20" className='dateIcons' onClick={() => {setReturnDatePickerOpen(true)}} src={rootUrl+'/assets/icons/new/search/input-icon-return-date (1).svg'} alt="icon-return-vehicle.svg" loading="lazy"/>}
                                                        <div onClick={() => {if (!isReturnDatePickerOpen) {setReturnDatePickerOpen(true)}}}>
                                                            <DatePicker
                                                                disabled={false}
                                                                open={isReturnDatePickerOpen}
                                                                onOpen={() =>{
                                                                    setReturnDatePickerOpen(true);
                                                                }}
                                                                onAccept={() => {setReturnDatePickerOpen(false)}}
                                                                onClose={() => {setReturnDatePickerOpen(false)}}
                                                                closeOnSelect={false}
                                                                disablePast
                                                                format="DD.MM.YYYY"
                                                                label={translateService.translate('vehicleSearch.returnDate')}
                                                                className={`vehicleSearch-inputs ${!props.modifyReservation ? 'dateInputsOneWay' : ''}`}
                                                                minDate={moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours")}
                                                                maxDate={moment(pickupDate).seconds(0).milliseconds(0).add(60, "days")}
                                                                value={returnDate}
                                                                onChange={(p) => handleReturnDateChange(p)}
                                                                slots={{openPickerIcon: MuiDateTimeIcons}}
                                                                slotProps={{
                                                                    textField: {
                                                                        variant: 'outlined',
                                                                        error: !!returnErrorMessage,
                                                                        helperText: returnErrorMessage
                                                                    },
                                                                    field: {readOnly: true},
                                                                    actionBar: {
                                                                        actions: ["cancel", "accept"],
                                                                    },
                                                                    popper: {
                                                                        popperOptions: {
                                                                            placement: !props.modifyReservation ? "bottom" : "auto"
                                                                        }
                                                                    },
                                                                    inputAdornment: {
                                                                        disablePointerEvents: true
                                                                    },
                                                                }}
                                                                onError={(error) => setReturnError(error)}
                                                            />
                                                        </div>
                                                </div>
                                                <div className={`${!props.modifyReservation ? 'dateInputWrapper col-12 position-relative col-md-6 col-lg-6  px-0' : 'col-12 position-relative px-0'}`}>
                                                    {!props.modifyReservation && <img width="20" height="20" className='dateIcons' onClick={() => {setReturnTimePickerOpen(true)}} src={rootUrl+'/assets/icons/new/search/input-icon-return-time (1).svg'} alt="icon-return-vehicle.svg" loading="lazy"/>}
                                                        <div onClick={() => {if (!isReturnTimePickerOpen) {setReturnTimePickerOpen(true)}}}>
                                                            <DesktopTimePicker
                                                                onOpen={() => {
                                                                    setReturnTimePickerOpen(true);
                                                                }}
                                                                open={isReturnTimePickerOpen}
                                                                onAccept={() => {
                                                                    setReturnDatePickerOpen(false);
                                                                    setReturnTimePickerOpen(false);
                                                                }}
                                                                onClose={() => {setReturnTimePickerOpen(false)}}
                                                                closeOnSelect={true}
                                                                label={translateService.translate('vehicleSearch.returnTime')}
                                                                format={"HH:mm"}
                                                                minutesStep={15}
                                                                skipDisabled
                                                                minTime={moment(pickupDate).isSame(returnDate, 'day') ? moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours") : moment()}
                                                                value={moment(returnDate).seconds(0).milliseconds(0)}
                                                                className={`vehicleSearch-inputs ${!props.modifyReservation ? 'dateInputsOneWay' : ''}`}
                                                                disabled={false}
                                                                onChange={(p) => handleReturnTimeChange(p)}
                                                                slots={timePickerSlots}
                                                                slotProps={timePickerSlotProps(returnErrorMessage)}
                                                                disableIgnoringDatePartForTimeValidation
                                                                ampm={false}
                                                            />
                                                        </div>
                                                </div>
                                            </>
                                    }
                                      </LocalizationProvider>
                            </ThemeProvider>
                        </div>
                        <div className={styles.oneWaySearchButtonContainer}>
                            <button
                                id='search-button'
                                type="submit"
                                disabled={(disableButton || (props.modifyReservation ?
                                    (searchParameters?.returnCity ? searchParameters?.returnCity.length < 1 : true) : (oneWayReturnCity.value).length === 0))}
                                className="btn btn-primary btn-outline-primary btn-cta w-50 font-weight-bold"
                                style={{marginTop: "10px"}}
                                onClick={(e) => {
                                    e.button === 0 && e.preventDefault(); // prevent double click
                                    document.getElementById("search-button")?.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'center'
                                    });
                                    if (sessionStorage.getItem('paymentSuccess') ?? undefined) {
                                        clearSelections();
                                    }
                                    handleSearchChanges();
                                    const startDate = pickupDate;
                                    const startTime = pickupDate.clone().format("HH:mm");
                                    const endDate = returnDate;
                                    const endTime = returnDate.clone().format("HH:mm");

                                        if (!checkDateTimeValues(startDate, endDate)) return;
                                        if (!sessionStorage.getItem('searchInit')) {
                                            sessionStorage.setItem('searchInit', '1');
                                        }

                                        if (!props.modifyReservation && (oneWayPickupCity.value).length && (oneWayReturnCity.value).length) {
                                            props.setSearchParams((params: ISearchParameters) => {
                                                return {
                                                    ...params,
                                                    startDate: startDate,
                                                    startTime: startTime,
                                                    endDate: endDate,
                                                    endTime: endTime,
                                                    km: 100,
                                                    showStart: 0,
                                                    showAmount: 10,
                                                    pickupDate: pickupDate,
                                                    returnDate: returnDate,
                                                    pickupCity: oneWayPickupCity.value,
                                                    returnCity: oneWayReturnCity.value,
                                                    carId: !props.modifyReservation ? null : params.carId,
                                                    cid: user?.id ?? customerBehaviorData.cid,
                                                    utm_campaign: customerBehaviorData.utm_campaign,
                                                    utm_source: customerBehaviorData.utm_source,
                                                    utm_term: customerBehaviorData.utm_term,
                                                    utm_medium: customerBehaviorData.utm_medium,
                                                    utm_content: customerBehaviorData.utm_content,
                                                    gclid: customerBehaviorData.gclid,
                                                    cookie: r24CookieVal
                                                };
                                            });
                                        } else {
                                            props.setSearchParams((params: ISearchParameters) => {
                                                return {
                                                    ...params,
                                                    startDate: startDate,
                                                    startTime: startTime,
                                                    endDate: endDate,
                                                    endTime: endTime,
                                                    pickupDate: pickupDate,
                                                    returnDate: returnDate,
                                                    carId: !props.modifyReservation ? null : params.carId,
                                                    cid: user?.id ?? customerBehaviorData.cid,
                                                    utm_campaign: customerBehaviorData.utm_campaign,
                                                    utm_source: customerBehaviorData.utm_source,
                                                    utm_term: customerBehaviorData.utm_term,
                                                    utm_medium: customerBehaviorData.utm_medium,
                                                    utm_content: customerBehaviorData.utm_content,
                                                    gclid: customerBehaviorData.gclid,
                                                    cookie: r24CookieVal
                                                };
                                            });
                                        }

                                        if (!(sessionStorage.getItem('oneWaySearchInit') ?? undefined)) {
                                            // indicator for default screen visibility
                                            sessionStorage.setItem('oneWaySearchInit', 'true')
                                        }
                                    }}>
                                    {!props.modifyReservation ?
                                        (oneWayPickupCity.value === '' || oneWayReturnCity.value === '') ?
                                            (oneWayPickupCity.value === '' ? translateService.translate('vehicleSearch.selectPickupCity')
                                                : translateService.translate('vehicleSearch.selectReturnCity'))
                                            : translateService.translate('vehicleSearch.find')
                                        : translateService.translate('vehicleSearch1.checkAvailability')}
                                </button>

                            </div>
                            <div>
                                {!props.modifyReservation ?
                                    (landBotReady ?
                                            <LandBot
                                                botId={owLandbotConfig[language === 'fi' ? 'fi' : 'en'].id}
                                                baseUrl={owLandbotConfig[language === 'fi' ? 'fi' : 'en'].baseUrl ?? undefined}
                                                buttonContent={
                                                    <div className="btn btn-secondary btn-outline-secondary btn-all w-50
                                                    float-right font-weight-bold mt-2">
                                                        {translateService.translate('vehicleSearch.questions')}
                                                    </div>}
                                                language={language}
                                                params={{
                                                    phase: "",
                                                    msg_token: "-1",
                                                    customer_id: -1,
                                                    is_leasing: "false",
                                                    requester: "",
                                                    reservation_id: -1,
                                                    vehicle_id: undefined,
                                                    model_id: undefined,
                                                }}
                                            /> :
                                            <CustomTooltip
                                                className="tooltip-link"
                                                zIndex={100}
                                                content={<Translate content={'vehicleSearch.onewayDoesNotWork'}/>}
                                                placement={"bottom"}>
                                                <div className="btn btn-secondary btn-outline-secondary btn-all w-50
                                            float-right font-weight-bold mt-2">
                                                    {translateService.translate('vehicleSearch.questions')}
                                                </div>
                                            </CustomTooltip>
                                    ) : null
                                }
                            </div>
                            <br/>
                            <div className={styles.oneWayDescription}>
                                {translateService.translate('vehicleSearch1.onewayDescription')}
                            </div>
                        </form>
                    : <form>
                        {!props.modifyReservation &&
                            <div className="position-relative mt-2">
                                <label id="searchbox-label"
                                    className={isLocationEmpty ? 'searchbox-label--empty px-1' : 'px-1 searchbox-label'}
                                    data-shrink="true"
                                    htmlFor="google-search-location">
                                    {translateService.translate('vehicleSearch.enterCity')}
                                </label>
                                <div className="position-relative" onKeyDown={(e) => {
                                    if (e.code === 'Enter') { // handle 'enter' click to prevent default vehicle search on page
                                        e.preventDefault();
                                        if (!(!location && (!searchParameters?.location || !searchParameters?.location.formatted_address) && !props.modifyReservation) || disableButton) {
                                            document.getElementById("search-button")?.focus();
                                        }
                                    }
                                }}>
                                    <div className="position-absolute" style={{top: '6px', left: "10px"}}>
                                        <img
                                            src={rootUrl + '/assets/icons/new/reservation-details/icon-magnifying-glass.svg'}
                                            alt="icon-magnifying-glass.svg" loading="lazy"
                                            width="14px" height="14px"/>
                                    </div>
                                    {mapsReady ? <Autocomplete
                                        ref={autoCompleteRef}
                                        id="google-search-location"
                                        name={locationFieldName}
                                        autoCorrect="off"
                                        autoCapitalize="off"
                                        spellCheck={false}
                                        className="w-100 searchbox-input"
                                        placeholder={translateService.translate('vehicleSearch.search')}
                                        defaultValue={getDefaultLocationValue()}
                                        onPlaceSelected={(place: google.maps.places.PlaceResult) => {
                                            if (place.geometry?.location?.lat() === undefined || place.geometry?.location?.lng() === null) {
                                                // if user does not click from suggestions, no geolocation data will be saved -->
                                                // last search will be used + ref will be use dto reset this field to last search data
                                                return;
                                            }

                                            setLat(place.geometry.location.lat());
                                            setLng(place.geometry.location.lng());
                                            setLocation(place)
                                            setIsAddressSelected(true);
                                            handleSearchChanges();
                                            setUserLocationSearchString(place.formatted_address ? place.formatted_address : '');
                                            document.getElementById('search-button')?.focus();
                                        }}
                                        options={{
                                            types: ['geocode', 'establishment'],
                                            componentRestrictions: { country: "fi" },

                                        }}
                                    /> : <input
                                        id="google-search-location"
                                        name={locationFieldName}
                                        autoCorrect="off"
                                        autoCapitalize="off"
                                        spellCheck={false}
                                        className="w-100 searchbox-input"
                                        placeholder={translateService.translate('vehicleSearch.search')}
                                        defaultValue={getDefaultLocationValue()}
                                    />}
                                    { autoCompleteRef?.current?.value && autoCompleteRef?.current?.value?.length > 0 &&
                                        <button type="button" // prevent form submission
                                            onClick={(e) => {
                                                e.preventDefault(); clearInput()}}
                                                className={styles['clearLocationField']}>x</button> }

                                </div>
                            </div>}
                        { enableOneWay && !props.modifyReservation ? <ResModeSwitch /> : null }
                        <div className='row mx-0 px-0'>
                            <ThemeProvider theme={yellowTheme}>
                                <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={language} localeText={language === 'en' ? enUS.components.MuiLocalizationProvider.defaultProps.localeText : fiFI.components.MuiLocalizationProvider.defaultProps.localeText}>
                                    { mobileDevice ?
                                        <>
                                            <div className={styles.dateTimeInputsRow}>
                                                <div className={`${!props.modifyReservation ? `dateInputWrapper position-relative pl-0 ${mobileDevice ? styles.mobileDateInput : ''}` 
                                                    : `position-relative px-0 ${mobileDevice ? styles.mobileDateInput : ''}`}`}>
                                                    {!props.modifyReservation &&  <img width="20" height="20" className='dateIcons' onClick={() => { if(isAddressSelected){setPickupDatePickerOpen(true)}}}
                                                                                       src={rootUrl+'/assets/icons/new/search/input-icon-pickup-date (1).svg'} alt="icon-pickup-vehicle.svg" loading="lazy"/>}
                                                   <MobileDatePicker
                                                            disabled={!isAddressSelected}
                                                            open={isPickupDatePickerOpen}
                                                            onAccept={() => {setPickupDatePickerOpen(false)}}
                                                            onOpen={() => {setPickupDatePickerOpen(true)}}
                                                            onClose={() => {setPickupDatePickerOpen(false)}}
                                                            className={`vehicleSearch-mobileDateInputs ${!props.modifyReservation ? 'dateInputs' : ''}`}
                                                            closeOnSelect
                                                            label={translateService.translate('vehicleSearch.pickupDate')}
                                                            disablePast
                                                            format="DD.MM.YYYY"
                                                            value={pickupDate}
                                                            onChange={(p) => handlePickupDateChange(p, true)}
                                                            slotProps={{
                                                                textField: {
                                                                    variant: 'outlined',
                                                                    error: !!pickupErrorMessage,
                                                                    helperText: pickupErrorMessage
                                                                }}}
                                                            onError={(error: any) => setPickupError(error)}
                                                        />
                                                </div>
                                                <div className={`${!props.modifyReservation ? `dateInputWrapper position-relative px-0 ${mobileDevice ? styles.mobileDateInput : ''}` 
                                                    : `position-relative px-0 ${mobileDevice ? styles.mobileDateInput : ''}`}`}>
                                                    {!props.modifyReservation &&  <img width="20" height="20" className='dateIcons' onClick={() => { if(isAddressSelected){setPickupTimePickerOpen(true)}}} src={rootUrl+'/assets/icons/new/search/input-icon-pickup-time (2).svg'} alt="icon-pickup-vehicle.svg" loading="lazy"/>}
                                                    <div onClick={() => {if (!isPickupTimePickerOpen && isAddressSelected) {setPickupTimePickerOpen(true)}}}>
                                                        <DesktopTimePicker
                                                            closeOnSelect={true}
                                                            open={isPickupTimePickerOpen}
                                                            onAccept={() => {setPickupTimePickerOpen(false)}}
                                                            onOpen={() => {setPickupTimePickerOpen(true)}}
                                                            onClose={() => {setPickupTimePickerOpen(false)}}
                                                            label={translateService.translate('vehicleSearch.pickupTime')}
                                                            format={"HH:mm"}
                                                            minutesStep={15}
                                                            skipDisabled
                                                            minTime={moment().isSame(pickupDate, 'day') ? moment().seconds(0).milliseconds(0) : moment()}
                                                            value={moment(pickupDate).seconds(0).milliseconds(0)}
                                                            className={`vehicleSearch-mobileDateInputs ${!props.modifyReservation ? 'dateInputs' : ''}`}
                                                            disabled={!isAddressSelected}
                                                            onChange={(p) => handlePickupTimeChange(p, true)}
                                                            slots={timePickerSlots}
                                                            slotProps={timePickerSlotProps(pickupErrorMessage)}
                                                            disableIgnoringDatePartForTimeValidation
                                                            ampm={false}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className={styles.dateTimeInputsRow}>
                                                <div className={`${!props.modifyReservation ? `dateInputWrapper position-relative pl-0 ${mobileDevice ? styles.mobileDateInput : ''}`
                                                    : `position-relative px-0 ${mobileDevice ? styles.mobileDateInput : ''}`}`}>
                                                    {!props.modifyReservation && <img width="20" height="20" className='dateIcons' onClick={() => { if(isAddressSelected){setReturnDatePickerOpen(true)}}} src={rootUrl+'/assets/icons/new/search/input-icon-return-date (1).svg'} alt="icon-return-vehicle.svg" loading="lazy"/>}
                                                    <MobileDatePicker
                                                        disabled={!isAddressSelected}
                                                        open={isReturnDatePickerOpen}
                                                        onOpen={() =>{setReturnDatePickerOpen(true)}}
                                                        onAccept={() => {setReturnDatePickerOpen(false)}}
                                                        onClose={() => {setReturnDatePickerOpen(false)}}
                                                        closeOnSelect={true}
                                                        disablePast
                                                        format="DD.MM.YYYY"
                                                        label={translateService.translate('vehicleSearch.returnDate')}
                                                        className={`vehicleSearch-mobileDateInputs ${!props.modifyReservation ? 'dateInputs' : ''}`}
                                                        minDate={moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours")}
                                                        maxDate={moment(pickupDate).seconds(0).milliseconds(0).add(60, "days")}
                                                        value={returnDate}
                                                        onChange={(p) => handleReturnDateChange(p, true)}
                                                        slotProps={{
                                                            textField: {
                                                                variant: 'outlined',
                                                                error: !!returnErrorMessage,
                                                                helperText: returnErrorMessage
                                                            }
                                                        }}
                                                        onError={(error) => setReturnError(error)}
                                                    />
                                                </div>
                                                <div className={`${!props.modifyReservation ? `dateInputWrapper position-relative px-0 ${mobileDevice ? styles.mobileDateInput : ''}`
                                                    : `position-relative px-0 ${mobileDevice ? styles.mobileDateInput : ''}`}`}>
                                                    {!props.modifyReservation && <img width="20" height="20" className='dateIcons' onClick={() => { if(isAddressSelected){setReturnTimePickerOpen(true)}}}
                                                                                      src={rootUrl+'/assets/icons/new/search/input-icon-return-time (1).svg'} alt="icon-return-vehicle.svg" loading="lazy"/>}
                                                    <div onClick={() => {if (!isReturnTimePickerOpen && isAddressSelected) {setReturnTimePickerOpen(true)}}}>
                                                        <DesktopTimePicker
                                                            onOpen={() => {
                                                                setReturnTimePickerOpen(true);
                                                            }}
                                                            open={isReturnTimePickerOpen}
                                                            onAccept={() => {
                                                                setReturnDatePickerOpen(false);
                                                                setReturnTimePickerOpen(false)}}
                                                            onClose={() => {setReturnTimePickerOpen(false)}}
                                                            closeOnSelect={true}
                                                            label={translateService.translate('vehicleSearch.returnTime')}
                                                            format={"HH:mm"}
                                                            minutesStep={15}
                                                            skipDisabled
                                                            minTime={moment(pickupDate).isSame(returnDate, 'day') ? moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours") : moment()}
                                                            value={moment(returnDate).seconds(0).milliseconds(0)}
                                                            className={`vehicleSearch-mobileDateInputs ${!props.modifyReservation ? 'dateInputs' : ''}`}
                                                            disabled={!isAddressSelected}
                                                            onChange={(p) => handleReturnTimeChange(p)}
                                                            slots={timePickerSlots}
                                                            slotProps={timePickerSlotProps(returnErrorMessage)}
                                                            disableIgnoringDatePartForTimeValidation
                                                            ampm={false}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                        : // DESKTOP COMPONENTS
                                        <>
                                            <div className={`${!props.modifyReservation ? 'dateInputWrapper col-12 position-relative col-md-6 col-lg-6 pl-0' : 'col-12 position-relative px-0'}`}>
                                                {!props.modifyReservation &&  <img width="20" height="20" className='dateIcons' onClick={() => { if(isAddressSelected){setPickupDatePickerOpen(true)}}} src={rootUrl+'/assets/icons/new/search/input-icon-pickup-date (1).svg'} alt="icon-pickup-vehicle.svg" loading="lazy"/>}
                                                <div onClick={() => {if (!isPickupDatePickerOpen && isAddressSelected) {setPickupDatePickerOpen(true)}}}>
                                                        <DatePicker
                                                            disabled={!isAddressSelected}
                                                            open={isPickupDatePickerOpen}
                                                            onAccept={() => {setPickupDatePickerOpen(false)}}
                                                            onOpen={() => {setPickupDatePickerOpen(true)}}
                                                            onClose={() => {setPickupDatePickerOpen(false);}}
                                                            className={`vehicleSearch-inputs ${!props.modifyReservation ? 'dateInputs' : ''}`}
                                                            closeOnSelect={false}
                                                            label={translateService.translate('vehicleSearch.pickupDate')}
                                                            disablePast
                                                            format="DD.MM.YYYY"
                                                            value={pickupDate}
                                                            onChange={(p) => handlePickupDateChange(p, true)}
                                                            slots={{
                                                                openPickerIcon: MuiDateTimeIcons,
                                                            }}
                                                            slotProps={{
                                                                textField: {
                                                                    variant: 'outlined',
                                                                    error: !!pickupErrorMessage,
                                                                    helperText: pickupErrorMessage
                                                                },
                                                                field: {readOnly: true},
                                                                actionBar: {
                                                                    actions: ["cancel", "accept"],
                                                                },
                                                                inputAdornment: {
                                                                    disablePointerEvents: true
                                                                },
                                                                popper: {
                                                                    popperOptions: {
                                                                        placement: !props.modifyReservation ? "bottom" : "auto"
                                                                    }
                                                                }
                                                            }}
                                                            onError={(error: any) => setPickupError(error)}
                                                        />
                                                    </div>
                                            </div>
                                            <div className={`${!props.modifyReservation ? 'dateInputWrapper col-12 position-relative col-md-6 col-lg-6  px-0' : 'col-12 position-relative px-0'}`}>
                                                {!props.modifyReservation &&  <img width="20" height="20" className='dateIcons' onClick={() => { if(isAddressSelected){setPickupTimePickerOpen(true)}}} src={rootUrl+'/assets/icons/new/search/input-icon-pickup-time (2).svg'} alt="icon-pickup-vehicle.svg" loading="lazy"/>}
                                                <div onClick={() => {if (!isPickupTimePickerOpen && isAddressSelected) {setPickupTimePickerOpen(true)}}}>
                                                        <DesktopTimePicker
                                                            closeOnSelect={true}
                                                            open={isPickupTimePickerOpen}
                                                            onAccept={() => {setPickupTimePickerOpen(false)}}
                                                            onOpen={() => {setPickupTimePickerOpen(true)}}
                                                            onClose={() => {setPickupTimePickerOpen(false)}}
                                                            label={translateService.translate('vehicleSearch.pickupTime')}
                                                            format={"HH:mm"}
                                                            minutesStep={15}
                                                            skipDisabled
                                                            minTime={moment().isSame(pickupDate, 'day') ? moment().seconds(0).milliseconds(0) : moment()}
                                                            value={moment(pickupDate).seconds(0).milliseconds(0)}
                                                            className={`vehicleSearch-inputs ${!props.modifyReservation ? 'dateInputs' : ''}`}
                                                            disabled={!isAddressSelected}
                                                            onChange={(p) => handlePickupTimeChange(p, true)}
                                                            slots={timePickerSlots}
                                                            slotProps={timePickerSlotProps(pickupErrorMessage)}
                                                            disableIgnoringDatePartForTimeValidation
                                                            ampm={false}
                                                        />
                                                </div>
                                            </div>
                                            <div className={`${!props.modifyReservation ? 'dateInputWrapper col-12 position-relative col-md-6 col-lg-6 pl-0' : 'col-12 position-relative px-0'}`}>
                                                {!props.modifyReservation && <img width="20" height="20" className='dateIcons' onClick={() => { if(isAddressSelected){setReturnDatePickerOpen(true)}}} src={rootUrl+'/assets/icons/new/search/input-icon-return-date (1).svg'} alt="icon-return-vehicle.svg" loading="lazy"/>}
                                               <div onClick={() => {if (!isReturnDatePickerOpen && isAddressSelected) {setReturnDatePickerOpen(true)}}}>
                                                        <DatePicker
                                                            disabled={!isAddressSelected}
                                                            open={isReturnDatePickerOpen}
                                                            onOpen={() =>{
                                                                setReturnDatePickerOpen(true);
                                                            }}
                                                            onAccept={() => {setReturnDatePickerOpen(false)}}
                                                            onClose={() => {setReturnDatePickerOpen(false)}}
                                                            closeOnSelect={false}
                                                            disablePast
                                                            format="DD.MM.YYYY"
                                                            label={translateService.translate('vehicleSearch.returnDate')}
                                                            className={`vehicleSearch-inputs ${!props.modifyReservation ? 'dateInputs' : ''}`}
                                                            minDate={moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours")}
                                                            maxDate={moment(pickupDate).seconds(0).milliseconds(0).add(60, "days")}
                                                            value={returnDate}
                                                            onChange={(p) => handleReturnDateChange(p, true)}
                                                            slots={{ openPickerIcon: MuiDateTimeIcons }}
                                                            slotProps={{
                                                                textField: {
                                                                    variant: 'outlined',
                                                                    error: !!returnErrorMessage,
                                                                    helperText: returnErrorMessage
                                                                },
                                                                field: {readOnly: true},
                                                                actionBar: {
                                                                    actions: ["cancel", "accept"],
                                                                },
                                                                popper: {
                                                                    popperOptions: {
                                                                        placement: !props.modifyReservation ? "bottom" : "auto"
                                                                    }
                                                                },
                                                                inputAdornment: {
                                                                    disablePointerEvents: true
                                                                },
                                                            }}
                                                            onError={(error) => setReturnError(error)}
                                                        />
                                               </div>

                                            </div>
                                            <div className={`${!props.modifyReservation ? 'dateInputWrapper col-12 position-relative col-md-6 col-lg-6  px-0' : 'col-12 position-relative px-0'}`} >
                                                {!props.modifyReservation && <img width="20" height="20" className='dateIcons' onClick={() => { if(isAddressSelected){setReturnTimePickerOpen(true)}}}
                                                                                  src={rootUrl+'/assets/icons/new/search/input-icon-return-time (1).svg'} alt="icon-return-vehicle.svg" loading="lazy"/>}
                                               <div onClick={() => {if (!isReturnTimePickerOpen && isAddressSelected) {setReturnTimePickerOpen(true)}}}>
                                                        <DesktopTimePicker
                                                            onOpen={() => {setReturnTimePickerOpen(true)}}
                                                            open={isReturnTimePickerOpen}
                                                            onAccept={() => {
                                                                setReturnDatePickerOpen(false);
                                                                setReturnTimePickerOpen(false);
                                                            }}
                                                            onClose={() => {setReturnTimePickerOpen(false)}}
                                                            closeOnSelect={true}
                                                            label={translateService.translate('vehicleSearch.returnTime')}
                                                            format={"HH:mm"}
                                                            minutesStep={15}
                                                            skipDisabled
                                                            minTime={moment(pickupDate).isSame(returnDate, 'day') ? moment(pickupDate).seconds(0).milliseconds(0).add(2, "hours") : moment()}
                                                            value={moment(returnDate).seconds(0).milliseconds(0)}
                                                            className={`vehicleSearch-inputs ${!props.modifyReservation ? 'dateInputs' : ''}`}
                                                            disabled={!isAddressSelected}
                                                            onChange={(p) => handleReturnTimeChange(p)}
                                                            slots={timePickerSlots}
                                                            slotProps={timePickerSlotProps(returnErrorMessage)}
                                                            disableIgnoringDatePartForTimeValidation
                                                            ampm={false}
                                                        />
                                               </div>
                                            </div>
                                        </>
                                    }
                                     </LocalizationProvider>
                            </ThemeProvider>
                        </div>
                        {props.modifyReservation ?
                            <button
                                id="search-button"
                                type="submit"
                                disabled={disableButton}
                                className="btn btn-primary btn-outline-primary btn-cta w-50 font-weight-bold float-right"
                                style={{marginTop: "10px"}}
                                onClick={(e) => submitSearch(e)}>
                                {translateService.translate('vehicleSearch1.checkAvailability')}
                            </button>
                            :
                            <div className="row mx-0 px-0" style={{marginTop: '10px', clear: 'both'}}>
                                {!(showDefaultResults && !location) && !mobileDevice &&
                                    <div className="col-12 col-md-6 col-lg-6 pl-0">
                                        <button
                                            id="search-button-van"
                                            type="submit"
                                            disabled={((!location && (!searchParameters?.location || !searchParameters?.location.formatted_address)) || disableButton)}
                                            className="btn btn-cta font-weight-bold w-100"
                                            style={{backgroundColor: '#0BB395', borderColor: '#0BB395', color: '#fff'}}
                                            onClick={(e) => submitSearch(e, 'van')}>
                                            {translateService.translate('vehicleSearch.findVan')}
                                        </button>
                                    </div>
                                }
                                <div className={`col-12 ${!(showDefaultResults && !location) ? 'col-md-6 col-lg-6' : ''} px-0`}>
                                    <button
                                        id="search-button"
                                        type="submit"
                                        disabled={((!location && (!searchParameters?.location || !searchParameters?.location.formatted_address)) || disableButton)}
                                        className="btn btn-primary btn-outline-primary btn-cta font-weight-bold w-100"
                                        onClick={(e) => submitSearch(e, 'car')}>
                                        {showDefaultResults && !location ? translateService.translate('vehicleSearch1.selectLocationAndDate') : translateService.translate('vehicleSearch.find')}
                                    </button>
                                </div>
                                {!(showDefaultResults && !location) && mobileDevice &&
                                    <div className="col-12 px-0" style={{marginTop: '10px'}}>
                                        <button
                                            id="search-button-van"
                                            type="submit"
                                            disabled={((!location && (!searchParameters?.location || !searchParameters?.location.formatted_address)) || disableButton)}
                                            className="btn btn-cta font-weight-bold w-100"
                                            style={{backgroundColor: '#0BB395', borderColor: '#0BB395', color: '#fff'}}
                                            onClick={(e) => submitSearch(e, 'van')}>
                                            {translateService.translate('vehicleSearch.findVan')}
                                        </button>
                                    </div>
                                }
                            </div>
                        }
                    </form>
                }
                {showPopup &&
                    <Dialog className='extension-modal' open={showPopup}>
                        <Card className={`card-body py-4`}>
                            <div className="text-center py-2">
                                <h2>{translateService.translate('vehicleSearch.hi')}</h2>
                                <span>{translateService.translate('vehicleSearch.OutOfRangeError1')}</span>
                                <br/>
                                <span>{translateService.translate('vehicleSearch.OutOfRangeError2')}</span>
                            </div>
                            <button className={'btn btn-primary btn-outline-primary btn-cta mt-2 w-50 m-auto'}
                                    onClick={() => setShowPopup(false)}>
                                <span>OK</span>
                            </button>
                        </Card>
                    </Dialog>}
                {showNoPickupCityMatch &&
                    <Dialog className='extension-modal' open={showNoPickupCityMatch}>
                        <Card className={`card-body py-4`}>
                            <div className="text-center py-2">
                                <span>{translateService.translate('vehicleSearch.cityNotInOnewayList')}</span>
                            </div>
                            <button className={'btn btn-primary btn-outline-primary btn-all mt-2 w-50 m-auto'}
                                    onClick={() => setShowNoPickupCityMatch(false)}>
                                <span>OK</span>
                            </button>
                        </Card>
                    </Dialog>}

            </Card>
        </>
    )
}
export default VehicleSearch;